import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { Form } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { CommonAlignCenterContainer, CommonButton, CommonInput } from "../AtomCommonComponents";
import { CommonFormItemWithLabel, CommonModal } from "../MoleculesCommonComponent";
import TextFormatUtils from "../../../utils/TextFormatUtils";
import ValidationUtils from "../../../utils/ValidationUtils";
import GoogleService from "../../../services/api/GoogleService";
import AddressSuggestionMarketModal from "./AddressSuggestionsMarketModal";
import DialogUtils from "../../../utils/DialogUtils";
import { AddEditAddressModalType, AddEditDataModalType } from "../../../types/types";
import APPConfig from "../../../config";

const { INITIAL_GEOLOCATION_LAT, INITIAL_GEOLOCATION_LONG } = APPConfig
const mapContainerStyle = {
  width: "450px",
  height: "340px",
};

interface AddEditAddressModalProps {
  isAddEditAddressModal: boolean;
  onCloseAddEditAddressModal: () => void;
  mode: string;
  setEditDataModal?: React.Dispatch<React.SetStateAction<AddEditAddressModalType>> | React.Dispatch<React.SetStateAction<AddEditDataModalType>>;
  setAddDataModal: React.Dispatch<React.SetStateAction<AddEditAddressModalType>> | React.Dispatch<React.SetStateAction<AddEditDataModalType>>;
  editDataModal?: AddEditDataModalType | AddEditAddressModalType;
  addDataModal: AddEditDataModalType | AddEditAddressModalType;
  title: string;
  onSaveAddress?: () => void;
}

const AddEditAddressModal = (props:AddEditAddressModalProps) => {
  const { mode, isAddEditAddressModal, setEditDataModal, setAddDataModal, onCloseAddEditAddressModal, editDataModal, addDataModal, onSaveAddress } = props;
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });

  const centerMarker = useMemo(() => {
    if(mode==="edit"){
      return {
        lat: editDataModal?.geolocation[0] ?? Number(INITIAL_GEOLOCATION_LAT),
        lng: editDataModal?.geolocation[1] ?? Number(INITIAL_GEOLOCATION_LONG),
      };
    }else{
      return {
        lat: addDataModal.geolocation[0] ? addDataModal.geolocation[0] : Number(INITIAL_GEOLOCATION_LAT),
        lng: addDataModal.geolocation[1] ? addDataModal.geolocation[1] : Number(INITIAL_GEOLOCATION_LONG),
      };
    }

  }, [mode, editDataModal, addDataModal]);

  const [markerAddress, setMarkerAddress] = useState(centerMarker);
  const [isAddressSuggestionModal, setAddressSuggestionModal] = useState<boolean>(false);
  const [addressSuggestion, setAddressSuggestion] = useState();
  const [form] = Form.useForm();
  const googleService = new GoogleService();
  
  useEffect(() => {
    const initialize = () => {
      if (isAddEditAddressModal) {
        if (mode === "add" && (!addDataModal.address_1 || !addDataModal.city)) {
          form.resetFields();
          setMarkerAddress({
            lat: 40.73,
            lng: -73.93,
          });
        } else if (mode === "add") {
          form.setFieldsValue(addDataModal)
          setMarkerAddress({
            lat: addDataModal.geolocation[0],
            lng: addDataModal.geolocation[1],
          });
        } else if (editDataModal && mode === "edit") {
          form.setFieldsValue(editDataModal);
          setMarkerAddress({
            lat: editDataModal.geolocation[0],
            lng: editDataModal.geolocation[1],
          });
        }
      }
    };
    initialize();
  }, [isAddEditAddressModal, form, editDataModal, addDataModal]);

  const onCloseSuggestedAddressModal = () => {
    setAddressSuggestionModal(false);
    onCloseAddEditAdressModal();
  }

  const onCloseAddEditAdressModal = () => {
    onCloseAddEditAddressModal();
    form.resetFields();
  };

  const onSubmitAddress = async () => {
    try {
      const errorAddressContent = "Provided address is not found. Please try another address."

      const values = await form.validateFields();
      values.address_2 = values.address_2 ? values.address_2 : "";
      if (mode === "edit") {

        const addressToCheck = {
          address_1: values.address_1,
          address_2: values.address_2,
          city: values.city,
          state: values.state,
          zip_code: values.zip_code,
          country: values.country,
        };

        const checkedResult = await googleService.checkAddress(addressToCheck);
        const addressSuggestions = checkedResult.address_details.address_suggestions;

        if (addressSuggestions?.length) {
          setAddressSuggestion(addressSuggestions);
          setAddressSuggestionModal(true);
        } else {
          const addressGeo = checkedResult.address_details?.location
          if(addressGeo){
            setEditDataModal && setEditDataModal((prevState:any) => ({
              ...prevState,
              address_1: values.address_1,
              address_2: values.address_2,
              city: values.city,
              country: values.country,
              zip_code: values.zip_code,
              state: values.state,
              geolocation: [addressGeo.lat, addressGeo.lng],
            }));
            onCloseAddEditAdressModal();
          }else{
            DialogUtils.error(errorAddressContent)
            return;
          }
        }
        setMarkerAddress({ lat: 0, lng: 0 });
      } else if (mode === "add") {
        // address_1, address_2, city, state, zip_code, country
        const addressToCheck = {
          address_1: values.address_1,
          address_2: values.address_2,
          city: values.city,
          state: values.state,
          zip_code: values.zip_code,
          country: values.country,
        };
        const checkedResult = await googleService.checkAddress(addressToCheck);
        const addressSuggestions = checkedResult.address_details.address_suggestions;

        if (addressSuggestions?.length) {
            setAddressSuggestion(addressSuggestions);
            setAddressSuggestionModal(true);
            onSaveAddress && onSaveAddress()
        } else {
          const addressGeo = checkedResult.address_details?.location
          if(addressGeo){
            setAddDataModal((prevState: any) => ({
              ...prevState,
              address_1: values.address_1,
              address_2: values.address_2,
              city: values.city,
              country: values.country,
              zip_code: values.zip_code,
              state: values.state,
              geolocation: [addressGeo.lat, addressGeo.lng],
            }));
            onSaveAddress && onSaveAddress()
            onCloseAddEditAdressModal();
          }
          else{
            DialogUtils.error(errorAddressContent)
            return;
          }
        }
        setMarkerAddress({ lat: 0, lng: 0 });
      }
    } catch (error) {
      console.error("Form validation error:", error);
    }
  };

  const modalFooter = [
    <CommonButton key="cancel" onClick={onCloseAddEditAdressModal}>
      Cancel
    </CommonButton>,
    <CommonButton key="save" color="white" background="primary" onClick={onSubmitAddress}>
      Save
    </CommonButton>,
  ];

  return (
    <>
      <CommonModal
        maskClosable={false}
        title={mode === "edit" && editDataModal?.address_1 && editDataModal?.city ? `Edit ${props.title} Address` : `Add ${props.title} Address`}
        visible={isAddEditAddressModal}
        onCancel={onCloseAddEditAdressModal}
        footer={modalFooter}
      >
        <>
          <div>
            <CommonFormItemWithLabel label={"Map"} rules={[{ required: true }]}>
              {isLoaded && mode && (
                <CommonAlignCenterContainer>
                  <GoogleMap
                    zoom={12}
                    center={centerMarker}
                    mapContainerStyle={mapContainerStyle}
                    options={{
                      streetViewControl: false,
                      fullscreenControl: false,
                      draggable: false,
                    }}
                  >
                    <Marker position={markerAddress} draggable={false} />
                  </GoogleMap>
                </CommonAlignCenterContainer>
              )}
            </CommonFormItemWithLabel>
          </div>
        </>
        <Form
          form={form}
          validateTrigger="onChange"
          onFinish={onSubmitAddress}
          initialValues={{
            country: "United States",
          }}
        >
          <CommonFormItemWithLabel label={"Address Line 1"} name="address_1" rules={[{ required: true, message: "Address Line 1 is required." }, { validator: ValidationUtils.validateInput }]}>
            <CommonInput
              placeholder="Input Address Line 1"
              onInput={(e: any) => {
                TextFormatUtils.upperText(e);
              }}
              color="primary"
            />
          </CommonFormItemWithLabel>
          <CommonFormItemWithLabel label={"City"} name="city" rules={[{ required: true, message: "City is required." }, { validator: ValidationUtils.validateInput }]}>
            <CommonInput
              placeholder="Input City"
              onInput={(e: any) => {
                TextFormatUtils.upperText(e);
              }}
              color="primary"
            />
          </CommonFormItemWithLabel>
          <CommonFormItemWithLabel label={"State"} name="state" rules={[{ required: true, message: "State is required." }, { validator: ValidationUtils.validateInput }]}>
            <CommonInput
              placeholder="Input State"
              onInput={(e: any) => {
                TextFormatUtils.upperText(e);
              }}
              color="primary"
            />
          </CommonFormItemWithLabel>
          <CommonFormItemWithLabel
            label={"Zip Code"}
            name="zip_code"
            rules={[
              { required: true, message: "Zip Code is required." },
              { validator: ValidationUtils.validateNumberInput },
              { pattern: /^\d{5}$/, message: "Please enter a valid 5-digit zip code." },
            ]}
          >
            <CommonInput color="primary" placeholder="Input Zip Code" />
          </CommonFormItemWithLabel>

          <CommonFormItemWithLabel label={"Country"} name="country" rules={[{ required: true }]} initialValue={"United States"}>
            <CommonInput
              onInput={(e: any) => {
                TextFormatUtils.upperText(e);
              }}
              color="primary"
              disabled
            />
          </CommonFormItemWithLabel>
        </Form>
      </CommonModal>
      <AddressSuggestionMarketModal
        mode={mode}
        setAddDataModal={setAddDataModal}
        setEditDataModal={setEditDataModal}
        addressSuggestionsData={addressSuggestion}
        isAddressSuggestionMarketModal={isAddressSuggestionModal}
        onCloseAddEditAddressModal={onCloseSuggestedAddressModal}
      />
    </>
  );
};
export default AddEditAddressModal;
